import { useNowContext, useServices, useStore } from "@hq/client-react";
import { useActionRegistry } from "@hq/command-palette";
import { OPEN_NOTE_ACTION, OPEN_TASK_ACTION } from "@hq/command-palette";
import { todayOf } from "@hq/core";
import { downloadExport, exportVault, journalFolderId } from "@hq/notes-ui";
import { toast, resolveShortcut, useShortcutPreferences } from "@z/ui";
import { activateTab, findPane, focusPane, neighbourPane, nextTab, prevTab } from "@hq/layout";
import { useEffect } from "react";
import { nextTheme, THEME_KV_KEY, type ThemePreference } from "../theme";
import type { PaneSpec } from "../workspace/paneTypes";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { useQuickCapture } from "./QuickCaptureContext";
import { useTemplatePicker } from "./TemplatePickerContext";

/**
 * Registers every app-level action with the palette. Feature packages never import each
 * other; this is where notes, tasks, navigation and layout meet (research.md Section 4).
 */
export function useRegisterActions(): void {
  const registry = useActionRegistry();
  const shortcuts = useShortcutPreferences();
  const ws = useWorkspace();
  const services = useServices();
  const store = useStore();
  const now = useNowContext();
  const quickCapture = useQuickCapture();
  const templatePicker = useTemplatePicker();

  // The registry is an external system; registering is a subscription with cleanup.
  useEffect(() => {
    const go = (spec: PaneSpec) => () => ws.open(spec);
    const register = (action: Parameters<typeof registry.register>[0]) => {
      const shortcut = resolveShortcut(shortcuts, action.id);
      return registry.register({
        ...action,
        ...(shortcut ? { shortcut } : {}),
        allowInEditor:
          shortcuts.definitions.find((definition) => definition.id === action.id)?.allowInEditor ??
          false,
      });
    };
    const unregisters = [
      register({
        id: "notes.new",
        title: "New note",
        group: "Notes",
        keywords: ["create", "note"],
        run: async (ctx) => {
          const folderId =
            typeof ctx.currentFolderId === "string" ? ctx.currentFolderId : undefined;
          const note = await services.notes.create(folderId ? { folderId } : {});
          ws.openNote(note.id, { autoFocus: true, pinned: true });
        },
      }),
      register({
        id: "notes.daily",
        title: "Today's daily note",
        group: "Notes",
        keywords: ["journal", "today"],
        run: async () => {
          const folders = await store.folders.all();
          const folderId = journalFolderId(folders);
          const note = await services.notes.openDaily(todayOf(now), folderId);
          ws.openNote(note.id, { autoFocus: true, pinned: true });
        },
      }),
      register({
        id: OPEN_NOTE_ACTION,
        title: "Open note",
        group: "Notes",
        when: (ctx) => typeof ctx.noteId === "string",
        run: (ctx) => ws.openNote(String(ctx.noteId)),
      }),
      register({
        id: "notes.export",
        title: "Export all notes as Markdown",
        group: "Notes",
        keywords: ["backup", "download", "vault"],
        run: async () => {
          const { files } = await exportVault(store);
          const result = await downloadExport(files);
          if (result === "cancelled") return;
          toast({
            title: "Export complete",
            description:
              result === "directory"
                ? `${files.length} file(s) written`
                : "Downloaded as one JSON bundle",
          });
        },
      }),
      register({
        id: "tasks.new",
        title: "Quick capture to Inbox",
        group: "Tasks",
        keywords: ["new task", "create", "capture", "todo", "inbox"],
        run: () => quickCapture.open(),
      }),
      register({
        id: OPEN_TASK_ACTION,
        title: "Open task",
        group: "Tasks",
        when: (ctx) => typeof ctx.taskId === "string",
        run: (ctx) => ws.openTask(String(ctx.taskId)),
      }),
      register({
        id: "go.today",
        title: "Go to Today",
        group: "Navigate",
        run: go({ paneType: "today" }),
      }),
      register({
        id: "go.inbox",
        title: "Go to Inbox",
        group: "Navigate",
        run: go({ paneType: "inbox" }),
      }),
      register({
        id: "go.anytime",
        title: "Go to Anytime",
        group: "Navigate",
        run: go({ paneType: "anytime" }),
      }),
      register({
        id: "go.upcoming",
        title: "Go to Upcoming",
        group: "Navigate",
        run: go({ paneType: "upcoming" }),
      }),
      register({
        id: "go.someday",
        title: "Go to Someday",
        group: "Navigate",
        run: go({ paneType: "someday" }),
      }),
      register({
        id: "go.completed",
        title: "Go to Completed",
        group: "Navigate",
        run: go({ paneType: "completed" }),
      }),
      register({
        id: "go.notes",
        title: "Go to All Notes",
        group: "Navigate",
        run: go({ paneType: "allNotes" }),
      }),
      register({
        id: "go.daily",
        title: "Go to Daily Notes",
        group: "Navigate",
        run: go({ paneType: "daily" }),
      }),
      register({
        id: "go.templates",
        title: "Go to Templates",
        group: "Navigate",
        run: go({ paneType: "templates" }),
      }),
      register({
        id: "go.calendar",
        title: "Go to Calendar",
        group: "Navigate",
        run: go({ paneType: "calendar" }),
      }),
      register({
        id: "go.dashboards",
        title: "Go to Dashboards",
        group: "Navigate",
        keywords: ["metrics", "panels"],
        run: go({ paneType: "dashboards" }),
      }),
      register({
        id: "notes.newFromTemplate",
        title: "New note from template",
        group: "Notes",
        keywords: ["template"],
        run: () => templatePicker.open(),
      }),
      register({
        id: "layout.pinTab",
        title: "Pin tab",
        group: "View",
        when: () => ws.focused !== undefined,
        run: () => {
          if (ws.focused) ws.pin(ws.focused.id);
        },
      }),
      register({
        id: "layout.closeOthers",
        title: "Close other tabs",
        group: "View",
        when: () => ws.focused !== undefined,
        run: () => {
          if (ws.focused) ws.closeOthers(ws.focused.id);
        },
      }),
      register({
        id: "layout.closeGroup",
        title: "Close all tabs in this group",
        group: "View",
        when: () => ws.focused !== undefined,
        run: () => {
          if (ws.focused) ws.closeGroup(ws.focused.id);
        },
      }),
      register({
        id: "go.trash",
        title: "Go to Trash",
        group: "Navigate",
        run: go({ paneType: "trash" }),
      }),
      register({
        id: "go.settings",
        title: "Open Settings",
        group: "Navigate",
        run: go({ paneType: "settings" }),
      }),
      register({
        id: "projects.new",
        title: "New project",
        group: "Tasks",
        run: async () => {
          const name = window.prompt("Project name");
          if (!name?.trim()) return;
          const p = await services.projects.create({ name });
          ws.open({ paneType: "project", projectId: p.id });
        },
      }),
      register({
        id: "view.theme.toggle",
        title: "Toggle dark mode",
        group: "View",
        keywords: ["theme", "light", "dark"],
        run: async () => {
          const current = await store.kv.get<ThemePreference>(THEME_KV_KEY);
          await store.kv.set(THEME_KV_KEY, nextTheme(current));
        },
      }),
      register({
        id: "layout.closePane",
        title: "Close pane",
        group: "View",
        when: () => ws.focused !== undefined,
        run: () => {
          if (ws.focused) ws.close(ws.focused.id);
        },
      }),
      ...(["left", "right", "up", "down"] as const).map((direction) =>
        register({
          id: `layout.focus.${direction}`,
          title: `Focus pane ${direction}`,
          group: "View",
          run: () => {
            if (!ws.focused) return;
            const target = neighbourPane(ws.tree, ws.focused.id, direction);
            if (target) ws.setTree(focusPane(ws.tree, target));
          },
        }),
      ),
      ...(["next", "previous"] as const).map((direction) =>
        register({
          id: direction === "next" ? "layout.nextTab" : "layout.previousTab",
          title: direction === "next" ? "Next tab" : "Previous tab",
          group: "View",
          run: () => {
            if (!ws.focused) return;
            const group = findPane(ws.tree, ws.focused.id)?.group;
            if (!group) return;
            const target = (direction === "next" ? nextTab : prevTab)(ws.tree, group.id);
            if (target) ws.setTree(activateTab(ws.tree, target));
          },
        }),
      ),
    ];
    return () => {
      for (const u of unregisters) u();
    };
  }, [registry, ws, services, store, now, quickCapture, templatePicker, shortcuts]);
}
